import { Check, Copy } from "lucide-react";
import { useState } from "react";

interface CodeBlockProps {
  code: string;
  language?: string;
  title?: string;
}

/* Light, hairline terminal — the same surface as the hero's command output.
   `$` prompts and `#` comments are tinted; everything else prints in ink. */
const renderLine = (line: string, i: number) => {
  if (line.startsWith("$ ")) {
    return (
      <span key={i} className="block">
        <span className="text-zinc-400">$ </span>
        {line.slice(2)}
      </span>
    );
  }
  if (line.trimStart().startsWith("#")) {
    return (
      <span key={i} className="block text-zinc-400">
        {line}
      </span>
    );
  }
  return (
    <span key={i} className="block">
      {line || "\u00a0"}
    </span>
  );
};

export const CodeBlock = ({ code, language, title }: CodeBlockProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-5 border border-zinc-200 bg-white">
      <div className="flex items-center justify-between border-b border-zinc-200/70 px-4 py-1.5">
        <span className="font-mono text-3xs uppercase tracking-[0.14em] text-zinc-400">
          {title ?? language ?? "code"}
        </span>
        <button
          type="button"
          aria-label={copied ? "Copied to clipboard" : "Copy code to clipboard"}
          title={copied ? "Copied to clipboard" : "Copy code to clipboard"}
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 font-mono text-3xs uppercase tracking-[0.14em] text-zinc-400 hover:text-zinc-900"
        >
          {copied ? <Check className="h-3 w-3" aria-hidden /> : <Copy className="h-3 w-3" aria-hidden />}
          <span>{copied ? "copied" : "copy"}</span>
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-3.5 text-sm leading-[1.7]">
        <code className="font-mono text-zinc-900">{code.split("\n").map(renderLine)}</code>
      </pre>
    </div>
  );
};

interface ParamTableProps {
  params: {
    name: string;
    type: string;
    required?: boolean;
    description: string;
    default?: string;
  }[];
}

export const ParamTable = ({ params }: ParamTableProps) => {
  return (
    <div className="my-5 overflow-x-auto border-t border-zinc-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="py-2 pr-4 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400">Parameter</th>
            <th className="py-2 pr-4 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400">Type</th>
            <th className="py-2 font-mono text-3xs font-normal uppercase tracking-[0.14em] text-zinc-400">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200/70 border-t border-zinc-200/70">
          {params.map((p) => (
            <tr key={p.name}>
              <td className="py-2.5 pr-4 align-top">
                <code className="font-mono text-xs text-zinc-900">{p.name}</code>
                {p.required && (
                  <span className="ml-1.5 font-mono text-3xs uppercase tracking-[0.1em] text-zinc-400">
                    required
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-4 align-top font-mono text-xs text-zinc-500">
                {p.type}
                {p.default && <span className="ml-1 text-zinc-400">= {p.default}</span>}
              </td>
              <td className="py-2.5 align-top text-zinc-600">{p.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
